import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Mail, Calendar, AlertCircle } from "lucide-react";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollStatus = async () => {
      try {
        const originUrl = window.location.origin;
        const response = await axios.get(`${API}/checkout/status/${sessionId}?origin_url=${encodeURIComponent(originUrl)}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          track("booking_paid", { session_id: sessionId, amount: response.data.amount_total });
          return;
        }
        
        if (response.data.status === "expired") {
          setStatus("expired");
          return;
        }

        // Continue polling
        if (attempts < maxAttempts) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus("pending");
        }
      } catch (error) {
        console.error("Error checking status:", error);
        if (attempts >= maxAttempts) {
          setStatus("error");
        } else {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center">
        {status === "checking" && (
          <div className="animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-800 border border-slate-700 mb-6">
              <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
            </div>
            <h1 className="font-serif text-3xl text-white mb-4">Verifying Payment</h1>
            <p className="text-slate-400">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#D4AF37]/20 border border-[#D4AF37] mb-6">
              <Check className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif text-3xl text-white mb-4">Payment Successful!</h1>
            <p className="text-slate-400 mb-8">
              Your booking has been confirmed. You will receive a confirmation email shortly with 
              pre-session forms and instructions.
            </p>

            <div className="bg-slate-900/50 border border-slate-800 p-6 mb-8 text-left">
              <h3 className="text-sm uppercase tracking-wider text-slate-400 mb-4">What's Next?</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#38BDF8] mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Check Your Email</p>
                    <p className="text-slate-400 text-sm">You'll receive a confirmation with session details and pre-session forms.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-[#38BDF8] mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Complete Pre-Session Forms</p>
                    <p className="text-slate-400 text-sm">Please complete the forms at least 24 hours before your session.</p>
                  </div>
                </div>
              </div>
            </div>

            <Link to="/">
              <Button 
                data-testid="return-home-btn"
                className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium px-8 py-6 rounded-none uppercase tracking-widest text-sm"
              >
                Return to Homepage
              </Button>
            </Link>
          </div>
        )}

        {status === "pending" && (
          <div className="animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-800 border border-slate-700 mb-6">
              <Loader2 className="w-10 h-10 text-[#38BDF8]" />
            </div>
            <h1 className="font-serif text-3xl text-white mb-4">Payment Processing</h1>
            <p className="text-slate-400 mb-8">
              Your payment is still being processed. Please check your email for confirmation shortly.
            </p>
            <Link to="/">
              <Button 
                className="bg-transparent border border-slate-700 text-slate-200 hover:border-slate-500 hover:text-white hover:bg-transparent px-8 py-6 rounded-none uppercase tracking-widest text-sm"
              >
                Return to Homepage
              </Button>
            </Link>
          </div>
        )}

        {(status === "error" || status === "expired") && (
          <div className="animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-500/20 border border-red-500 mb-6">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-serif text-3xl text-white mb-4">
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h1>
            <p className="text-slate-400 mb-8">
              {status === "expired" 
                ? "Your payment session has expired. Please try booking again."
                : "We couldn't verify your payment. Please try again or contact support."}
            </p>
            <Link to="/">
              <Button 
                data-testid="try-again-btn"
                className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium px-8 py-6 rounded-none uppercase tracking-widest text-sm"
              >
                Try Again
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingSuccess;
